import React, { ReactNode } from 'react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { BUTTON_THEMES, ICON_TYPES } from '../../../shared/constants';
import { IContentfulLink } from '../../../types/shared';

import Link from '../../atoms/link/Link';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3 } from '../../../styles/generic';

interface Props {
  index: number;
  image: string | IGatsbyImageData;
  title?: string;
  buttonTheme?: string;
  ctaLinkPrimary?: IContentfulLink;
  ctaLinkSecondary?: IContentfulLink;
  children?: ReactNode;
}

const PreviewCard = ({
  index,
  image,
  title,
  buttonTheme,
  ctaLinkPrimary,
  ctaLinkSecondary,
  children,
}: Props) => {
  const trackEvent = (productLink?: string) => {
    if (!window.aabTracker || !productLink) return;

    window.aabTracker.trackEvent({
      event: 'click',
      data: {
        contentInteractionName: 'linkClick',
        linkTitle: `${productLink}`,
        linkDestination: productLink,
      },
    });
  };

  const productPrimaryLink = ctaLinkPrimary
    ? getCtaLink(ctaLinkPrimary)
    : undefined;
  const productSecondaryLink = ctaLinkSecondary
    ? getCtaLink(ctaLinkSecondary)
    : undefined;

  const returnImage = (cardImage: string | IGatsbyImageData) => {
    const string = String(cardImage);

    if (string.includes('.svg')) {
      return (
        <div>
          <ImageContainer>
            <Image src={image as string} />
          </ImageContainer>
          {title && <Title data-testid="title">{title}</Title>}
        </div>
      );
    }
    if (image as IGatsbyImageData)
      return (
        <GatsbyImage
          alt=""
          image={{ ...(image as IGatsbyImageData), height: 0.66 }}
        />
      );

    return <div>{title && <Title data-testid="title">{title}</Title>}</div>;
  };

  return (
    <Container id={`product-id-${index}`} key={`product-key-${index}`}>
      {ctaLinkPrimary || ctaLinkSecondary ? (
        <ImageLink
          data-testid="link"
          href={productPrimaryLink || productSecondaryLink || '/404/'}
          customClick={() =>
            trackEvent(productPrimaryLink || productSecondaryLink)
          }
          trackingComponent="sales-block"
          trackingType={DEQ_TYPES.btnImage}
          trackingLabel={title || ''}
        >
          {returnImage(image)}
        </ImageLink>
      ) : (
        returnImage(image)
      )}
      <TextContainer data-testid="text-container">
        <StyledBody>{children}</StyledBody>
        {ctaLinkPrimary && (
          <StyledPrimaryButton
            buttonTheme={ctaLinkPrimary.theme || buttonTheme}
            to={productPrimaryLink}
            className="button"
            deqComponentTitle={ctaLinkPrimary.label}
            type={ctaLinkPrimary.type}
            icon={ICON_TYPES.none}
            label={ctaLinkPrimary.label}
            deqCustomType="sales-block"
            deqCustomAttributes={{
              componentType: 'sales-block',
              type: 'btn-base',
              elementTarget: productPrimaryLink,
            }}
          />
        )}
        {ctaLinkSecondary && (
          <StyledSecondaryButton
            buttonTheme={BUTTON_THEMES.default}
            to={productSecondaryLink}
            className="link"
            deqComponentTitle={ctaLinkSecondary.label}
            label={ctaLinkSecondary.label}
            icon={ctaLinkSecondary.icon}
            type={ctaLinkSecondary.type}
            iconPosition={ctaLinkSecondary.iconPosition}
            deqCustomAttributes={{
              componentType: 'sales-block',
              type: DEQ_TYPES.textLink,
              label: ctaLinkSecondary.label,
              elementTarget: productSecondaryLink,
            }}
          />
        )}
      </TextContainer>
    </Container>
  );
};

export default PreviewCard;

const Container = styled.div`
  background: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationTile};
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ImageLink = styled(Link)`
  background: ${Colors.abnMutedGreen50};

  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`;

const ImageContainer = styled.div`
  margin: 0 auto;
  max-height: 104px;
  max-width: 104px;
  padding: 24px 0 8px;
  position: relative;

  ${mq.from.breakpoint.L`
    padding: 40px 0 8px;
  `}
`;

const Image = styled.img`
  height: 100%;
  object-fit: cover;
  transition: all 0.1s ease-in-out;
  width: 100%;
`;

const TextContainer = styled.div`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  padding: 24px;

  ${mq.from.breakpoint.XL`
    padding: 32px 40px;
  `}
`;

const StyledBody = styled(Body)`
  margin-bottom: 0;

  > h3 {
    margin-bottom: 8px;
    margin-top: 0;
  }

  p:not(:first-child),
  p:not(:last-child) {
    margin-bottom: 16px;
  }

  p ~ ul,
  ul {
    padding-top: 0;
  }

  ${mq.from.breakpoint.M`
    margin-bottom: 16px;
  `}
`;

const Title = styled(H3)`
  color: ${Colors.abnMutedGreen500};
  margin-bottom: 24px;
  margin-top: 0;
  padding: 0 16px;
  text-align: center;

  ${mq.from.breakpoint.L`
    margin-bottom: 40px;
  `}
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  justify-content: center;
  margin-top: auto;
  text-align: center;
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  justify-content: flex-start;
  margin-top: 24px;
  text-align: left;
`;
